import { MailOutlined, ScheduleOutlined, SendOutlined } from "@ant-design/icons"
import { Alert, Button, Checkbox, Flex, Form, message, Modal, Select, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"

import { getEmailTemplates } from "../../../../EmailTemplates/api/services.ts"
import type { EmailTemplate as CompanyEmailTemplate } from "../../../../EmailTemplates/types/email-template.types.ts"
import { getLeadEmailTemplates, uploadLeadEmailImage } from "../../../api/services.ts"
import type {
  LeadEmailCampaignRequest,
  LeadEmailCampaignResult,
  LeadEmailTemplate
} from "../../../api/services.types.ts"
import type { BasePagination } from "../../../../../types/api.ts"
import { LEAD_BULK_LIMIT } from "../../../LeadsActivePage/components/leads-list-table/LeadsBulkActionBar.tsx"
import { cn } from "../../../../../utils/cn.ts"
import { OverviewCardIcon } from "../../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"
import { RichTextEditor } from "../../../../../components/RichTextEditor/RichTextEditor.tsx"
import { isRichTextEmpty } from "../../../../../components/RichTextEditor/rich-text-html.ts"
import { CustomDatePicker } from "../../../../../components/Date&TimePickers/CustomDatePicker.tsx"
import { CustomTimePicker } from "../../../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { TABLE_BULK_BAR_CLASS } from "../../../../../components/Table/table-bulk-bar.ts"
import { combineScheduledFor } from "./lead-email-schedule.ts"

// The composer's "template" select mixes two unrelated backend models: the
// personal, self-service `crm.EmailTemplate` this composer already used, and
// the company-wide `templates_management.EmailTemplate` library. Prefixing
// the option value is what tells `applyTemplate`/`submit` which list (and
// which behavior) a selection came from without a second piece of state.
const PERSONAL_PREFIX = "personal:"
const COMPANY_PREFIX = "company:"

const { Text } = Typography

type Delivery = "now" | "schedule"

type ComposerValues = {
  template?: string
  subject?: string
  body?: string
  save_as_template_name?: string
  delivery: Delivery
  scheduled_date?: Dayjs
  scheduled_time?: Dayjs
}

interface LeadsInactiveBulkBarProps {
  selectedIds?: string[]
  leadNames?: Record<string, string>
  editAllowed?: boolean
  onClear: () => void
  /** Goes through `leads.thunks.sendLeadEmailCampaign`, which owns the
   *  envelope — the component never unwraps one itself. */
  onSend: (params: LeadEmailCampaignRequest) => Promise<LeadEmailCampaignResult>
}

function DeliveryOptionCard({
  checked,
  icon,
  title,
  description,
  onClick,
  disabled
}: {
  checked: boolean
  icon: React.ReactNode
  title: string
  description: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <Flex
      role={"radio"}
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={title}
      tabIndex={disabled ? -1 : 0}
      align={"center"}
      gap={"small"}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (disabled) return
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onClick()
        }
      }}
      className={cn(
        "flex-1 cursor-pointer rounded-lg border p-3 transition-colors",
        checked ? "border-primary bg-primary-bg" : "border-border hover:border-primary/50",
        disabled && "cursor-not-allowed opacity-60"
      )}
    >
      <span
        className={cn(
          "size-4 shrink-0 rounded-full border-2",
          checked ? "border-primary bg-primary" : "border-border"
        )}
      />
      <OverviewCardIcon icon={icon} accent={"teal"} />
      <Flex vertical gap={0}>
        <Text strong>{title}</Text>
        <Text type={"secondary"} className={"text-xs"}>
          {description}
        </Text>
      </Flex>
    </Flex>
  )
}

export function LeadsInactiveBulkBar({
  selectedIds = [],
  leadNames = {},
  editAllowed,
  onClear,
  onSend
}: LeadsInactiveBulkBarProps) {
  const { t } = useTranslation()
  // The account's configured format, not the browser's.
  const { dateFormat } = useDateFormatter()
  const [form] = Form.useForm<ComposerValues>()
  const [composing, setComposing] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [templates, setTemplates] = useState<LeadEmailTemplate[]>([])
  const [companyTemplates, setCompanyTemplates] = useState<
    CompanyEmailTemplate[]
  >([])
  const [result, setResult] = useState<LeadEmailCampaignResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)
  // Read to switch the CTA label/icon and to show/hide the date+time fields —
  // `Form.useWatch` needs the form mounted, which it is for as long as the
  // modal is open.
  const delivery = Form.useWatch("delivery", form)
  const scheduling = delivery === "schedule"

  // `isStale` lets a caller that can race (the open/close effect below, on a
  // quick reopen) suppress a response that lands after a newer request has
  // already started; `submit()`'s post-save refresh doesn't race anything,
  // so it calls this with no argument.
  const loadTemplates = useCallback(async (isStale: () => boolean = () => false) => {
    const response = await getLeadEmailTemplates()
    if (isStale() || !response.ok) return
    setTemplates(
      unwrapEnvelope<BasePagination<LeadEmailTemplate>>(response.data)
        ?.results ?? []
    )
  }, [])

  // The company library has no per-composer save path (it's Settings-managed)
  // — loaded once per open, never refreshed the way `loadTemplates` is after
  // a personal save.
  const loadCompanyTemplates = useCallback(async (isStale: () => boolean = () => false) => {
    const response = await getEmailTemplates({ page_size: 1000 })
    if (isStale() || !response.ok) return
    setCompanyTemplates(response.data?.results ?? [])
  }, [])

  useEffect(() => {
    if (!composing) return
    let cancelled = false
    const isStale = () => cancelled
    void loadTemplates(isStale)
    void loadCompanyTemplates(isStale)
    return () => {
      cancelled = true
    }
  }, [composing, loadTemplates, loadCompanyTemplates])

  const handleImageUpload = async (file: File) => {
    const response = await uploadLeadEmailImage(file)
    const url = unwrapEnvelope<{ url: string }>(response.data)?.url
    if (!response.ok || !url) {
      void message.error(t("leadEmail.validation.imageUploadFailed"))
      throw new Error("Image upload failed")
    }
    return { url }
  }

  const overLimit = selectedIds.length > LEAD_BULK_LIMIT

  const applyTemplate = (value?: string) => {
    if (!value) return
    // Overwrites rather than fills the blanks: picking a template is asking
    // for that template's wording, and a half-replaced message is nobody's.
    if (value.startsWith(COMPANY_PREFIX)) {
      const id = value.slice(COMPANY_PREFIX.length)
      const template = companyTemplates.find((candidate) => candidate.id === id)
      // The Message field is real rich text now, and the company library's
      // body is already sanitized HTML — filled through unchanged, same as
      // a personal template's body below.
      if (template) {
        form.setFieldsValue({
          subject: template.subject,
          body: template.body
        })
      }
      return
    }
    if (!value.startsWith(PERSONAL_PREFIX)) return
    const id = value.slice(PERSONAL_PREFIX.length)
    const template = templates.find((candidate) => candidate.id === id)
    if (template) form.setFieldsValue({ subject: template.subject, body: template.body })
  }

  const close = () => {
    setComposing(false)
    setSavingTemplate(false)
    form.resetFields()
  }

  const submit = async () => {
    let values: ComposerValues
    try {
      values = await form.validateFields()
    } catch {
      // The form renders its own field errors; letting the rejection escape
      // would surface as an unhandled promise from antd's onOk.
      return
    }
    setSubmitting(true)
    try {
      // The backend's `template` field only resolves against the personal
      // crm.EmailTemplate model (it's a server-side subject/body fallback,
      // never persisted on the campaign) — a company-library pick is a pure
      // client-side prefill (subject/body are already required fields by the
      // time this runs) and must never be forwarded as `template`, or the
      // PrimaryKeyRelatedField lookup 400s against the wrong table.
      const personalTemplateId = values.template?.startsWith(PERSONAL_PREFIX)
        ? values.template.slice(PERSONAL_PREFIX.length)
        : undefined
      const sent = await onSend({
        ids: selectedIds,
        subject: values.subject,
        body: values.body,
        ...(personalTemplateId ? { template: personalTemplateId } : {}),
        ...(savingTemplate && values.save_as_template_name
          ? { save_as_template_name: values.save_as_template_name }
          : {}),
        ...(values.delivery === "schedule" &&
        values.scheduled_date &&
        values.scheduled_time
          ? {
              // Sent without an offset on purpose: the server reads a bare
              // wall-clock time in the sender's own timezone, which is the
              // one they picked it in.
              scheduled_for: combineScheduledFor(
                values.scheduled_date,
                values.scheduled_time
              ).format("YYYY-MM-DDTHH:mm:ss")
            }
          : {})
      })
      setRequestFailed(false)
      setResult(sent)
      // The send happened; leaving the rows ticked invites a second campaign to
      // the same people. The bar stays up to report the outcome.
      onClear()
      if (savingTemplate && values.save_as_template_name) void loadTemplates()
      close()
    } catch {
      setResult(null)
      setRequestFailed(true)
      close()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  // The response's own bucket, not the recipients: deriving it from an outcome
  // would call a delivered lead "not emailed".
  const notEmailed = result?.failed ?? []

  const visible = !!selectedIds.length || !!result || requestFailed
  if (!visible) return null

  const buttonClass =
    "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

  return (
    <div
      className={TABLE_BULK_BAR_CLASS}
      data-testid="leads-inactive-bulk-bar"
    >
      {selectedIds.length ? (
        <Flex
          align="center"
          gap="small"
          className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
          role="toolbar"
          aria-label={t("leadEmail.toolbarLabel")}
          wrap={false}
        >
          <Text strong className="mr-2 whitespace-nowrap !text-white">
            {t("leadBulk.selected", { count: selectedIds.length })}
          </Text>

          {overLimit ? (
            <Text className="mr-2 whitespace-nowrap !text-amber-300">
              {t("leadEmail.overLimit", { limit: LEAD_BULK_LIMIT })}
            </Text>
          ) : null}

          {editAllowed ? (
            <Button
              className={buttonClass}
              disabled={overLimit}
              onClick={() => setComposing(true)}
            >
              {t("leadEmail.actions.sendEmail")}
            </Button>
          ) : null}

          <Button type="text" className="!text-white" onClick={clear}>
            {t("leadBulk.clearSelection")}
          </Button>
        </Flex>
      ) : null}

      {requestFailed ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setRequestFailed(false)}
          message={t("leadEmail.result.requestFailed")}
        />
      ) : null}

      {result ? (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => setResult(null)}
          message={t("leadEmail.result.summary", {
            queued: result.succeeded.length,
            requested: result.requested
          })}
          description={
            notEmailed.length ? (
              <ul
                data-testid="lead-email-result-exceptions"
                tabIndex={0}
                aria-label={t("leadEmail.result.exceptionsLabel", {
                  count: notEmailed.length
                })}
                className="mb-0 max-h-[30vh] list-disc overflow-y-auto pl-5"
              >
                {notEmailed.map((failure) => (
                  <li key={failure.id}>
                    {t("leadEmail.result.exception", {
                      lead: leadNames[failure.id] || failure.id,
                      reason: failure.message
                    })}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
      ) : null}

      <Modal
        open={composing}
        centered
        maskClosable={false}
        closable={!submitting}
        keyboard={!submitting}
        title={null}
        footer={
          <Flex align={"center"} justify={"space-between"}>
            <Text type={"secondary"}>
              {t("leadEmail.composer.recipient", { count: selectedIds.length })}
            </Text>
            <Flex gap={"small"}>
              <Button disabled={submitting} onClick={close}>
                {t("leadBulk.cancel")}
              </Button>
              <Button
                type={"primary"}
                icon={
                  scheduling ? (
                    <ScheduleOutlined aria-hidden />
                  ) : (
                    <SendOutlined aria-hidden />
                  )
                }
                loading={submitting}
                onClick={submit}
              >
                {scheduling
                  ? t("leadEmail.composer.scheduleEmail")
                  : t("leadEmail.composer.sendEmail")}
              </Button>
            </Flex>
          </Flex>
        }
        onCancel={close}
        destroyOnHidden
      >
        <Flex align={"flex-start"} gap={"middle"} className={"mb-4"}>
          <OverviewCardIcon icon={<MailOutlined />} accent={"teal"} size={40} />
          <Flex vertical gap={0}>
            <Text strong className={"text-base"}>
              {t("leadEmail.composer.title", { count: selectedIds.length })}
            </Text>
            <Text type={"secondary"}>{t("leadEmail.composer.subtitle")}</Text>
          </Flex>
        </Flex>

        <Form<ComposerValues>
          form={form}
          layout="vertical"
          preserve={false}
          disabled={submitting}
          initialValues={{ delivery: "now" }}
        >
          <Form.Item name="template" label={t("leadEmail.fields.template")}>
            <Select
              allowClear
              placeholder={t("leadEmail.placeholders.template")}
              onChange={applyTemplate}
              options={[
                ...(companyTemplates.length
                  ? [
                      {
                        label: t("leadEmail.fields.companyTemplates"),
                        options: companyTemplates.map((template) => ({
                          value: `${COMPANY_PREFIX}${template.id}`,
                          label: template.name
                        }))
                      }
                    ]
                  : []),
                ...(templates.length
                  ? [
                      {
                        label: t("leadEmail.fields.myTemplates"),
                        options: templates.map((template) => ({
                          value: `${PERSONAL_PREFIX}${template.id}`,
                          label: template.name
                        }))
                      }
                    ]
                  : [])
              ]}
            />
          </Form.Item>

          <Form.Item
            name="subject"
            label={t("leadEmail.fields.subject")}
            rules={[
              { required: true, message: t("leadEmail.validation.subject") }
            ]}
          >
            <TextInput placeholder={t("leadEmail.placeholders.subject")} />
          </Form.Item>

          <Form.Item
            name="body"
            label={t("leadEmail.fields.body")}
            rules={[
              {
                validator: (_rule, value?: string) =>
                  !isRichTextEmpty(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("leadEmail.validation.body")))
              }
            ]}
          >
            <RichTextEditor
              variant={"compact"}
              minHeight={140}
              ariaLabel={t("leadEmail.fields.body")}
              value={""}
              onChange={() => undefined}
              disabled={submitting}
              placeholder={t("leadEmail.placeholders.body")}
              onImageUpload={handleImageUpload}
            />
          </Form.Item>

          <Form.Item className={"mb-2"}>
            <Checkbox
              checked={savingTemplate}
              onChange={(event) => setSavingTemplate(event.target.checked)}
            >
              {t("leadEmail.fields.saveAsTemplate")}
            </Checkbox>
          </Form.Item>

          {savingTemplate ? (
            <Form.Item
              name="save_as_template_name"
              label={t("leadEmail.fields.saveAsTemplateName")}
              rules={[
                {
                  required: true,
                  message: t("leadEmail.validation.saveAsTemplateName")
                }
              ]}
            >
              <TextInput
                placeholder={t("leadEmail.placeholders.saveAsTemplateName")}
              />
            </Form.Item>
          ) : null}

          <Text strong className={"mb-2 block"}>
            {t("leadEmail.composer.delivery")}
          </Text>
          <Form.Item name="delivery" className={"mb-3"}>
            <Flex gap={"small"} role={"radiogroup"} aria-label={t("leadEmail.composer.delivery")}>
              <DeliveryOptionCard
                checked={!scheduling}
                disabled={submitting}
                icon={<SendOutlined />}
                title={t("leadEmail.composer.sendNow")}
                description={t("leadEmail.composer.sendNowDescription")}
                onClick={() => form.setFieldValue("delivery", "now")}
              />
              <DeliveryOptionCard
                checked={scheduling}
                disabled={submitting}
                icon={<ScheduleOutlined />}
                title={t("leadEmail.composer.scheduleLater")}
                description={t("leadEmail.composer.scheduleLaterDescription")}
                onClick={() => form.setFieldValue("delivery", "schedule")}
              />
            </Flex>
          </Form.Item>

          {scheduling ? (
            <Flex gap={"middle"}>
              <Form.Item
                name="scheduled_date"
                label={t("leadEmail.fields.scheduledDate")}
                className={"flex-1"}
                rules={[
                  {
                    required: true,
                    message: t("leadEmail.validation.scheduledDate")
                  }
                ]}
              >
                <CustomDatePicker format={dateFormat} className={"w-full"} />
              </Form.Item>

              {/* CustomTimePicker is the shared scheduling standard (Calendar,
               * Lead forms): a 12-hour clock with AM/PM inside its own picker
               * panel, not a bolted-on control beside it. */}
              <Form.Item
                name="scheduled_time"
                label={t("leadEmail.fields.scheduledTime")}
                className={"flex-1"}
                rules={[
                  {
                    required: true,
                    message: t("leadEmail.validation.scheduledTime")
                  },
                  {
                    // Also refused server-side; this only saves a round trip.
                    validator: (_rule, value?: Dayjs) => {
                      const date = form.getFieldValue("scheduled_date") as
                        | Dayjs
                        | undefined
                      if (!value || !date) return Promise.resolve()
                      return combineScheduledFor(date, value).isAfter(dayjs())
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(t("leadEmail.validation.scheduledInPast"))
                          )
                    }
                  }
                ]}
              >
                <CustomTimePicker className={"w-full"} />
              </Form.Item>
            </Flex>
          ) : null}
        </Form>
      </Modal>
    </div>
  )
}
